// Cloudflare Turnstile for this app: the quote-request form, and the sign-in.
//
// Why it exists for RFQ (2026-09-16): leads were written straight from this browser into `freight_rfq`
// through PostgREST, under an anon INSERT policy whose check was literally `true` — anyone, with no
// account, could write any row. A probe used it three times that day, posting `{}` and getting three
// rows of pure database defaults into a table the sales desk reads. The write now goes through
// POST /v1/rfq on the family intake API, which verifies the token this file produces.
//
// Why it exists for AUTH (2026-09-23): this app signs people in against the SAME Supabase project as
// TrackingMCP. Supabase's "Enable CAPTCHA protection" is a PROJECT-WIDE switch — the moment it is
// turned on, GoTrue demands a token on every magic link, sign-up, password sign-in and reset for every
// app on that project. An app that sends no token does not degrade: its sign-in stops working
// outright. This file is what lets that switch be flipped without taking this product's login down.
//
// THE RULE: a real person must never be asked to do anything. Turnstile runs in `interaction-only`
// appearance, which renders NOTHING for a visitor who passes passively — nearly all of them. A widget
// appears only for a session Cloudflare cannot clear on its own. Every failure path (blocked script,
// network down, timeout, widget error) resolves to null rather than throwing, because a broken
// challenge must never be the reason a real customer cannot get in or send a lead.
//
// Kept deliberately in step with TrackingMCP's copy (apps/web/src/lib/turnstile.ts). Fix a bug here,
// fix it there.

declare global {
  interface Window {
    turnstile?: {
      render: (el: HTMLElement, opts: Record<string, unknown>) => string
      remove: (id: string) => void
    }
  }
}

/** Empty until configured, and empty means "no challenge" everywhere below — so shipping this ahead of
 *  the key changes nothing. */
const SITEKEY = (import.meta.env.VITE_TURNSTILE_SITEKEY as string | undefined) ?? ''

// How long a challenge may take before we stop waiting.
//
// It was 8s until 2026-09-23, on the reasoning that a real widget answers fast. It does not always.
// Measured against the production widget that day: it sat on "Verifying…" for 9s and 14s in two runs
// before it said anything at all. Eight seconds did not time out a broken challenge — it cut off a
// live one and handed the caller null, which on the auth path is a refused sign-in.
//
// The number a deadline has to beat is how long a SLOW SUCCESS takes, not how long a person is willing
// to wait — because the alternative to waiting is not a faster answer, it is a refused one. Auth pays
// nothing for it in practice: the challenge starts when the page opens (see prewarmAuthChallenge).
const CHALLENGE_TIMEOUT_MS = 30_000

let scriptLoad: Promise<void> | null = null

function loadTurnstile(): Promise<void> {
  scriptLoad ??= new Promise((resolve, reject) => {
    const el = document.createElement('script')
    // The path carries a version — /turnstile/v0/api.js. Without it Cloudflare answers 404, the script
    // never loads, and every challenge silently resolves to "no token". That is not a hypothesis: the
    // `/v0/` was missing here from the day this file was written until 2026-09-23, so the RFQ form
    // produced no token for anyone and POST /v1/rfq refused every lead it was sent. The same typo cost
    // TrackingMCP twelve hours of locked-out sign-ins on 2026-09-17.
    el.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
    el.async = true
    el.onload = () => resolve()
    el.onerror = () => reject(new Error('turnstile blocked'))
    document.head.appendChild(el)
  })
  return scriptLoad
}

/**
 * Set once the widget has proved it cannot run here — a bad sitekey, or a hostname the widget was
 * never configured for. Without it, every attempt pays the full deadline before failing the same way
 * it failed a moment ago.
 *
 * WHAT MAY SET IT: only a Cloudflare 110xxx code, the family that means the WIDGET is wrong. Nothing
 * else. A deadline must not set it — silence also means a challenge still working, and a customer
 * whose first attempt was merely slow would then be told "no" instantly for the rest of the page's
 * life. Per page load, deliberately: a reload is a fair second chance.
 */
let widgetIsUnusable = false

/**
 * Cloudflare's 110xxx client error codes: the sitekey or the domain is wrong, i.e. OUR configuration.
 * Every other code (600xxx, 300xxx, …) is an execution failure that a retry can legitimately clear.
 */
function isWidgetMisconfigured(code: string): boolean {
  return /^110\d{3}$/.test(code)
}

/** Test seam: a page that never reloads during a test run would otherwise keep the flag forever. */
export function resetTurnstileHealth(): void {
  widgetIsUnusable = false
}

/**
 * Resolve a Turnstile token, or null.
 *
 * Null on anything going wrong — no key, blocked script, widget error, or a widget that loads and then
 * says nothing at all. That last case is why there is a hard deadline as well as Turnstile's own
 * timeout callback: the latter only fires when the widget is alive enough to fire it, and a promise
 * that never settles here is a submit button that spins forever.
 */
export async function solveTurnstile(timeoutMs = CHALLENGE_TIMEOUT_MS): Promise<string | null> {
  if (!SITEKEY) return null
  if (widgetIsUnusable) return null
  try {
    await loadTurnstile()
    if (!window.turnstile) return null
    return await new Promise<string | null>((resolve) => {
      // `interaction-only` draws nothing for a visitor who passes passively, so for almost everyone
      // this element stays empty and invisible wherever it sits. The exception is the whole point of
      // it: the visitor Cloudflare cannot clear on its own gets a checkbox to tick, and a checkbox
      // 16px into a corner is a control nobody notices. Bottom centre, above everything.
      const host = document.createElement('div')
      host.style.position = 'fixed'
      host.style.bottom = '24px'
      host.style.left = '50%'
      host.style.transform = 'translateX(-50%)'
      host.style.zIndex = '9999'
      document.body.appendChild(host)
      let settled = false
      const done = (t: string | null) => {
        if (settled) return
        settled = true
        clearTimeout(bail)
        try {
          if (id) window.turnstile?.remove(id)
        } catch {
          /* already gone */
        }
        host.remove()
        resolve(t)
      }
      // The hard stop. Nothing came back in thirty seconds: that is a dead challenge, but it is NOT
      // evidence the widget is misconfigured, so the next attempt is allowed to try again.
      const bail = setTimeout(() => done(null), timeoutMs)
      const id = window.turnstile?.render(host, {
        sitekey: SITEKEY,
        appearance: 'interaction-only',
        // Let the widget retry itself on a transient failure instead of the caller eating the first
        // one. 600xxx is an execution failure, not a verdict. The deadline above still bounds the
        // whole thing, so a retry loop cannot outlive the submit.
        retry: 'auto',
        'retry-interval': 2_000,
        'refresh-expired': 'auto',
        callback: (token: string) => done(token),
        // Returning true tells Turnstile to retry; returning false ends the widget. Only a
        // configuration error ends it, because only a configuration error cannot come good on a retry.
        'error-callback': (code?: unknown) => {
          if (isWidgetMisconfigured(String(code ?? ''))) {
            widgetIsUnusable = true
            done(null)
            return false
          }
          return true
        },
        'timeout-callback': () => done(null),
      })
      if (!id) done(null)
    })
  } catch {
    return null
  }
}

/**
 * A captcha token for a Supabase auth call, in the shape supabase-js expects.
 *
 * Returns `undefined` — not null, not an empty string — when there is no key or the challenge did not
 * produce one, so the result can be spread straight into `options` and simply not be there:
 *
 *     await authClient.auth.signInWithOtp({ email, options: { ...(await authCaptchaOptions()) } })
 *
 * GoTrue is the enforcer, not this function. With captcha switched off in the project it ignores the
 * field; with captcha switched on it rejects a request that arrives without a valid one.
 */
export async function authCaptchaOptions(): Promise<{ captchaToken?: string }> {
  if (!SITEKEY) return {}
  const token = await (takeWarmChallenge() ?? solveTurnstile())
  return token ? { captchaToken: token } : {}
}

// ── Starting the challenge before the person needs it ────────────────────────
//
// The deadline above has to beat a SLOW SUCCESS, which is why it is thirty seconds. That is the right
// number for "how long may a challenge take", and the wrong thing to make a human watch. Both facts
// hold at once if the challenge starts when the page OPENS rather than when the button is pressed: a
// person spends ten to thirty seconds typing an address, the widget spends that same time working, and
// by the click the token is usually already in hand.
//
// A token is single-use and Cloudflare expires it after five minutes, so a warmed one is taken exactly
// once and is not trusted after four.
const WARM_TOKEN_TTL_MS = 4 * 60 * 1000
let warmChallenge: Promise<string | null> | null = null
let warmStartedAt = 0

/**
 * Begin an auth challenge now, in the background. Call it when an auth page mounts. Safe to call
 * repeatedly: a challenge already in flight is left alone, so a re-render does not start a second one.
 */
export function prewarmAuthChallenge(): void {
  if (!SITEKEY) return
  if (warmChallenge && Date.now() - warmStartedAt < WARM_TOKEN_TTL_MS) return
  warmStartedAt = Date.now()
  // Never rejects — solveTurnstile resolves null on every failure path — but the catch keeps an
  // unhandled rejection impossible if that ever changes.
  warmChallenge = solveTurnstile().catch(() => null)
}

/** The warmed challenge, if one is in flight and still young. Consumes it: a token is single-use. */
function takeWarmChallenge(): Promise<string | null> | null {
  if (!warmChallenge) return null
  const fresh = Date.now() - warmStartedAt < WARM_TOKEN_TTL_MS
  const pending = warmChallenge
  warmChallenge = null
  return fresh ? pending : null
}

/** Test seam: forget any warmed challenge between cases. */
export function resetWarmChallenge(): void {
  warmChallenge = null
  warmStartedAt = 0
}

/**
 * Turn a GoTrue captcha rejection into a sentence the person can act on.
 *
 * THE DISRUPTION THIS EXISTS TO PREVENT: a real customer running a strict ad blocker, a privacy
 * extension, or a corporate network that blocks challenges.cloudflare.com never gets a token. Their
 * request reaches GoTrue without one and comes back as "captcha protection: request disallowed" — a
 * sentence that tells them nothing and looks like our service is broken.
 *
 * It does NOT refuse to make the call when the challenge fails: the client cannot know whether a token
 * was required, so we only translate a refusal that has actually happened.
 *
 * Returns null for any other error, so ordinary failures keep their own wording.
 */
export function captchaErrorMessage(err: { message?: string } | null | undefined): string | null {
  const m = String(err?.message ?? '')
  if (!/captcha/i.test(m)) return null
  // Never send someone to go and change their own setup for a fault we cannot rule out as ours. On
  // 2026-09-17 four customers spent their morning disabling extensions and switching browsers to fix a
  // misconfiguration on our side. Say what happened and give them a route that works right now.
  return 'The security check could not be completed — this is usually a fault on our side, not yours. Signing in with Google or GitHub works right now, and support@trackingmcp.com will get you in if you are stuck.'
}
